import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Extension, type Editor, type Range } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import styled from '@emotion/styled';
import {
  Notes,
  Title,
  TextFields,
  FormatListBulleted,
  FormatListNumbered,
  type SvgIconComponent,
} from '@mui/icons-material';
import { uiTheme as t } from 'Components/ui';

export interface SlashMenuLabels {
  paragraph: string;
  heading1: string;
  heading2: string;
  bulletList: string;
  orderedList: string;
  /** aria-label del listbox flotante. */
  menu: string;
}

export interface SlashItem {
  key: 'paragraph' | 'heading1' | 'heading2' | 'bulletList' | 'orderedList';
  label: string;
  icon: SvgIconComponent;
  /** Aplica el bloque tras borrar el "/query" tecleado. */
  run: (editor: Editor, range: Range) => void;
}

/**
 * Catálogo del menú de slash: solo los bloques que el editor ya permite
 * (p, h1, h2, ul, ol). Nada de blockquote/código/etc.: siguen apagados y el
 * HTML del form no cambia de vocabulario.
 */
export const buildSlashItems = (labels: Omit<SlashMenuLabels, 'menu'>): SlashItem[] => [
  {
    key: 'paragraph',
    label: labels.paragraph,
    icon: Notes,
    run: (editor, range) => editor.chain().focus().deleteRange(range).setParagraph().run(),
  },
  {
    key: 'heading1',
    label: labels.heading1,
    icon: Title,
    run: (editor, range) => editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run(),
  },
  {
    key: 'heading2',
    label: labels.heading2,
    icon: TextFields,
    run: (editor, range) => editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run(),
  },
  {
    key: 'bulletList',
    label: labels.bulletList,
    icon: FormatListBulleted,
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    key: 'orderedList',
    label: labels.orderedList,
    icon: FormatListNumbered,
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
];

/** Sin acentos ni mayúsculas: "/tit" encuentra "Título". */
const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const filterSlashItems = (items: SlashItem[], query: string): SlashItem[] =>
  items.filter(item => normalize(item.label).includes(normalize(query)));

// ---------------------------------------------------------------------------
// Popup (lista flotante)
// ---------------------------------------------------------------------------

const Menu = styled.div({
  minWidth: 190,
  padding: 4,
  borderRadius: t.radius.sm,
  background: t.color.surface,
  border: `1px solid ${t.color.border}`,
  boxShadow: t.shadow.cardHover,
});

const Option = styled.button<{ selected: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  border: 'none',
  padding: '7px 9px',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
  background: selected ? t.color.countBg : 'transparent',
  color: selected ? t.color.primary : t.color.title,
  fontFamily: t.font,
  fontSize: 13,
  transition: 'background 0.12s ease, color 0.12s ease',
  '&:hover': { background: t.color.countBg },
  '& .MuiSvgIcon-root': { fontSize: 17, color: selected ? t.color.primary : t.color.muted },
}));

interface SlashMenuListProps {
  items: SlashItem[];
  ariaLabel: string;
  command: (item: SlashItem) => void;
}

export interface SlashMenuListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

/**
 * Lista navegable del menú de slash. El teclado (↑/↓/Enter) entra por el
 * `onKeyDown` del suggestion, que la extensión le reenvía vía ref.
 */
export const SlashMenuList = forwardRef<SlashMenuListRef, SlashMenuListProps>(({ items, ariaLabel, command }, ref) => {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (items.length === 0) return false;
      if (event.key === 'ArrowDown') {
        setSelected(current => (current + 1) % items.length);
        return true;
      }
      if (event.key === 'ArrowUp') {
        setSelected(current => (current + items.length - 1) % items.length);
        return true;
      }
      if (event.key === 'Enter') {
        command(items[selected]);
        return true;
      }
      return false;
    },
  }));

  if (items.length === 0) return null;

  return (
    <Menu role="listbox" aria-label={ariaLabel}>
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <Option
            key={item.key}
            type="button"
            role="option"
            aria-selected={index === selected}
            selected={index === selected}
            onMouseDown={event => event.preventDefault()} /* que el clic no le robe el foco al editor */
            onClick={() => command(item)}
          >
            <Icon />
            {item.label}
          </Option>
        );
      })}
    </Menu>
  );
});

SlashMenuList.displayName = 'SlashMenuList';

// ---------------------------------------------------------------------------
// Extensión
// ---------------------------------------------------------------------------

/**
 * Menú de slash estilo Notion sobre @tiptap/suggestion: "/" al inicio de
 * línea o tras un espacio abre el popup, lo tecleado filtra y al elegir se
 * aplica el bloque y se borra el "/query". El popup se posiciona a mano con
 * el clientRect del suggestion (sin tippy: menos piezas móviles).
 */
export const createSlashMenu = (labels: SlashMenuLabels) => {
  const items = buildSlashItems(labels);

  return Extension.create({
    name: 'slashMenu',

    addProseMirrorPlugins() {
      return [
        Suggestion<SlashItem>({
          editor: this.editor,
          pluginKey: new PluginKey('slashMenu'),
          char: '/',
          command: ({ editor, range, props: item }) => item.run(editor, range),
          items: ({ query }) => filterSlashItems(items, query),
          render: () => {
            let component: ReactRenderer<SlashMenuListRef, SlashMenuListProps> | null = null;
            let popup: HTMLDivElement | null = null;
            let closed = false;

            const position = (clientRect?: (() => DOMRect | null) | null) => {
              const rect = clientRect?.();
              if (!popup || !rect) return;
              popup.style.left = `${rect.left}px`;
              popup.style.top = `${rect.bottom + 6}px`;
            };

            const destroy = () => {
              popup?.remove();
              popup = null;
              component?.destroy();
              component = null;
            };

            return {
              onStart: (props: SuggestionProps<SlashItem>) => {
                closed = false;
                component = new ReactRenderer(SlashMenuList, {
                  editor: props.editor,
                  props: { items: props.items, ariaLabel: labels.menu, command: props.command },
                });
                popup = document.createElement('div');
                popup.style.position = 'fixed';
                popup.style.zIndex = '1400'; /* sobre el drawer */
                popup.appendChild(component.element);
                document.body.appendChild(popup);
                position(props.clientRect);
              },
              onUpdate: props => {
                if (closed || !component) return;
                component.updateProps({ items: props.items, command: props.command });
                position(props.clientRect);
              },
              onKeyDown: props => {
                if (closed) return false;
                if (props.event.key === 'Escape') {
                  closed = true;
                  destroy();
                  return true;
                }
                return component?.ref?.onKeyDown(props) ?? false;
              },
              onExit: destroy,
            };
          },
        }),
      ];
    },
  });
};
